import { useEffect, useState } from 'react';
import { Star, Quote } from 'lucide-react';
import { fetchPublicTestimonials } from '../api/testimonials';

const TESTIMONIAL_AVATAR_URL = '/branding/logo-white-closed.png';

function normalizeRating(value) {
  const rating = Number(value);
  if (!Number.isFinite(rating)) return 5;
  if (rating < 1) return 1;
  if (rating > 5) return 5;
  return Math.round(rating);
}

export default function Testimonials() {
  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let isMounted = true;

    const loadTestimonials = async () => {
      setIsLoading(true);
      setError('');

      try {
        const data = await fetchPublicTestimonials(12);
        if (!isMounted) return;
        setItems(Array.isArray(data) ? data : []);
      } catch (err) {
        if (!isMounted) return;
        setItems([]);
        setError(err instanceof Error ? err.message : 'Sizden Gelenler yüklenirken bir hata oluştu.');
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadTestimonials();

    return () => {
      isMounted = false;
    };
  }, []);

  return (
    <section className="section-padding bg-background-dark relative overflow-hidden">
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,_#2b6cee_1px,_transparent_1px)] bg-[length:20px_20px]" />
      </div>

      <div className="container-custom relative z-10">
        <div className="text-center mb-16">
          <span
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full
                          bg-primary/10 text-primary text-sm font-semibold mb-4
                          border border-primary/20"
          >
            <Quote className="w-4 h-4" />
            BAŞARI HİKAYELERİ
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-black mb-4 text-white">
            Sizden <span className="text-primary">Gelenler</span>
          </h2>
          <p className="text-slate-400 max-w-2xl mx-auto text-lg">
            Türkiye'nin önde gelen emlak profesyonelleri Maplio deneyimlerini paylaşıyor.
          </p>
        </div>

        {isLoading ? (
          <div className="max-w-4xl mx-auto rounded-2xl border border-slate-700/60 bg-slate-800/40 p-6 text-center text-slate-300">
            Gorusler yukleniyor...
          </div>
        ) : null}

        {!isLoading && error ? (
          <div className="max-w-4xl mx-auto rounded-2xl border border-red-500/40 bg-red-500/10 p-6 text-center text-red-200">
            {error}
          </div>
        ) : null}

        {!isLoading && !error && items.length === 0 ? (
          <div className="max-w-4xl mx-auto rounded-2xl border border-slate-700/60 bg-slate-800/40 p-6 text-center text-slate-300">
            Henuz yayinlanmis musteri gorusu bulunmuyor.
          </div>
        ) : null}

        {!isLoading && !error && items.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {items.map((testimonial) => (
              <div
                key={testimonial.id}
                className="bg-slate-800/50 backdrop-blur-sm p-8 rounded-3xl
                           border border-slate-700/50 hover:border-primary/50
                           transition-all duration-300 group"
              >
                <div className="flex items-center gap-4 mb-6">
                  <div className="relative">
                    <img
                      src={TESTIMONIAL_AVATAR_URL}
                      alt={testimonial.fullName}
                      className="w-16 h-16 rounded-full object-contain bg-slate-900 p-2 border-2
                                 border-primary/30 shadow-lg"
                      loading="lazy"
                    />
                    <div className="absolute -right-1 -bottom-1 bg-primary text-white p-1.5 rounded-full">
                      <Quote className="w-3 h-3" />
                    </div>
                  </div>
                  <div>
                    <h4 className="font-bold text-white text-lg">{testimonial.fullName}</h4>
                    <p className="text-slate-400 text-xs">{testimonial.roleTitle}</p>
                    <p className="text-primary text-xs font-medium">{testimonial.companyName}</p>
                  </div>
                </div>

                <div className="flex gap-1 mb-4">
                  {[...Array(normalizeRating(testimonial.rating))].map((_, index) => (
                    <Star key={index} className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                  ))}
                </div>

                <p className="text-slate-300 italic leading-relaxed text-sm">"{testimonial.content}"</p>
              </div>
            ))}
          </div>
        ) : null}
      </div>
    </section>
  );
}
